import React, { useState, useRef, useEffect, useCallback } from 'react';
import './CityAutocomplete.css';

const CityAutocomplete = ({
  value,
  onChange,
  className,
  placeholder = 'Search for a city...',
  label,
  required = false,
  error = ''
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search function
  const searchCities = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);

    try {
      // Using Nominatim API (OpenStreetMap) - free, no API key needed
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&` +
        `format=json&` +
        `addressdetails=1&` +
        `limit=8&` +
        `featuretype=city`,
        {
          headers: {
            'Accept-Language': 'en',
            'User-Agent': 'MovenMuse/1.0'
          }
        }
      );

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();

      // Filter and format results for cities
      const cityResults = data
        .filter(item =>
          item.type === 'city' ||
          item.type === 'town' ||
          item.type === 'village' ||
          item.addresstype === 'city' ||
          item.addresstype === 'town' ||
          item.addresstype === 'village' ||
          item.class === 'place'
        )
        .map(item => {
          const city = item.address?.city ||
                       item.address?.town ||
                       item.address?.village ||
                       item.name;
          const country = item.address?.country || '';
          const formatted = city && country ? `${city}, ${country}` : city || item.display_name;

          return {
            id: item.place_id,
            city,
            country,
            formatted,
            display_name: item.display_name
          };
        })
        .filter((item, index, self) =>
          // Remove duplicates based on formatted name
          index === self.findIndex(t => t.formatted === item.formatted)
        );

      setSuggestions(cityResults);
      setShowDropdown(cityResults.length > 0);
      setHighlightedIndex(-1);
    } catch (err) {
      console.error('City search error:', err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce search by 300ms
    debounceRef.current = setTimeout(() => {
      searchCities(newValue);
    }, 300);

    // Emit the raw value while typing
    onChange({ target: { name: 'city', value: newValue } });
  };

  // Handle selection from dropdown
  const handleSelect = (suggestion) => {
    setInputValue(suggestion.formatted);
    setSuggestions([]);
    setShowDropdown(false);

    onChange({ target: { name: 'city', value: suggestion.formatted } });
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle clear
  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    onChange({ target: { name: 'city', value: '' } });
    inputRef.current?.focus();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !inputRef.current?.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync with external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const hasError = error || className?.includes('has-error');

  return (
    <div className="city-autocomplete">
      {label && (
        <label className="city-label">
          {label}
          {required && <span className="required-asterisk">*</span>}
        </label>
      )}

      <div className={`city-input-container ${hasError ? 'has-error' : ''}`}>
        <i className="bi bi-geo-alt city-icon"></i>
        <input
          ref={inputRef}
          type="text"
          name="city"
          className="city-input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {isLoading && (
          <div className="loading-indicator">
            <div className="spinner-small"></div>
          </div>
        )}
        {inputValue && !isLoading && (
          <button
            type="button"
            className="clear-btn"
            onClick={handleClear}
            title="Clear"
          >
            <i className="bi bi-x"></i>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="city-dropdown" ref={dropdownRef}>
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              className={`city-dropdown-item ${index === highlightedIndex ? 'highlighted' : ''}`}
              onClick={() => handleSelect(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <i className="bi bi-geo-alt-fill"></i>
              <span>{suggestion.formatted}</span>
            </div>
          ))}
          <div className="osm-attribution">
            Data © OpenStreetMap contributors
          </div>
        </div>
      )}

      {error && <div className="city-error">{error}</div>}
    </div>
  );
};

export default CityAutocomplete;
